// apps/fe/src/modules/catalog/ui/pages/base-catalog-page.tsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { EventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogEvents, CatalogEventKeys } from '../../events';
import { Typography, Skeleton, Fade } from '@mui/material';
import NavigationButton from 'src/components/layout/navigation-button';
import { CoursesSection } from '../courses/catalog-courses-section';
import { ICourseData } from '../../types/course-data';
import { CourseDataFilters } from '../../types/filtered-data';
import { CatalogFilters } from '../../types/filter';
import { Instrument } from '../../types/instrument';

interface BaseCatalogPageProps {
  dict: CatalogDict;
  eventBus: EventBus<CatalogEvents>;
  isLoggedIn: boolean;
  filteredItems: ICourseData[];
  loadingCatalogData: boolean;
  filteringCatalogData: boolean;
  activeFilters: CourseDataFilters;
  parentInstruments: Instrument[];
  filters: CatalogFilters;
  categories?: any;
  pageTitle?: string;
  showBackButton?: boolean;
  renderTopComponent?: React.ReactNode;
  lastCourseElementRef: (node: HTMLElement | null) => void;
  instrumentsFilterOnChange: (checked: boolean, instrument: Instrument) => void;
  handleSkillLevelsChange: (selectedLevels: string[] | null) => void;
  handleGenresChange: (genre: string, checked: boolean) => void;
  handleTopicsChange: (topic: string, checked: boolean) => void;
  handleTypeChange: (type: string, checked: boolean) => void;
  onFavoriteChange?: (courseId: string, favouriteId: string | undefined) => void;
  isEnrichingData?: boolean;
  pageType?: 'home' | 'category' | 'search';
  onResetSidebarFilters: () => void;
  isAnyFilterActive: boolean;
  useSkeleton?: boolean;
}

/**
 * Base component for all catalog pages.
 * Provides common layout and functionality.
 *
 * Memoized to prevent unnecessary rerenders
 */
export const BaseCatalogPage = React.memo(function BaseCatalogPage({
  dict,
  eventBus,
  filteredItems,
  loadingCatalogData,
  filteringCatalogData,
  activeFilters,
  parentInstruments,
  filters,
  categories,
  pageTitle,
  showBackButton = false,
  renderTopComponent,
  lastCourseElementRef,
  instrumentsFilterOnChange,
  handleSkillLevelsChange,
  handleGenresChange,
  handleTopicsChange,
  handleTypeChange,
  onFavoriteChange,
  isEnrichingData = false,
  onResetSidebarFilters,
  isAnyFilterActive,
  useSkeleton = true,
}: BaseCatalogPageProps) {
  // Separate content loading from filter loading
  const [contentLoading, setContentLoading] = useState(loadingCatalogData || isEnrichingData);

  // Filters should never be in a loading state as they come from SSG
  const filterLoading = false;
  const [viewReady, setViewReady] = useState(false);

  // Debounce active filters to prevent rapid rerenders
  const [debouncedFilters, setDebouncedFilters] = useState(activeFilters);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Effect for handling smooth transitions of loading state
  useEffect(() => {
    if (loadingCatalogData || isEnrichingData) {
      setContentLoading(true);
    } else {
      // Small delay to prevent content flash
      const timer = setTimeout(() => {
        setContentLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loadingCatalogData, isEnrichingData]);

  // Initial view ready state
  useEffect(() => {
    // Simulate a short loading time to indicate view readiness
    const timer = setTimeout(() => {
      setViewReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Debounce filter changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilters(activeFilters);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [activeFilters]);

  if (!viewReady) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton animation="wave" variant="rounded" height={150} className="rounded-xl" />
        <Skeleton animation="wave" variant="rounded" height={450} className="rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {showBackButton && (
        <NavigationButton
          label="Indietro"
          onClick={() => eventBus.emit(CatalogEventKeys.navigateBack.do, undefined)}
        />
      )}

      {pageTitle && (
        <Typography
          variant="h3"
          className="self-stretch my-auto text-center sm:text-left"
          component="h3"
        >
          {pageTitle}
        </Typography>
      )}

      {renderTopComponent}

      <div className="flex flex-col gap-5">
        <CoursesSection
          dict={dict}
          eventBus={eventBus}
          filters={filters}
          activeFilters={debouncedFilters} // Use debounced filters
          parentInstruments={parentInstruments}
          instrumentsFilterOnChange={instrumentsFilterOnChange}
          filteringCatalogData={filteringCatalogData}
          loadingCatalogData={contentLoading}
          filteredItems={filteredItems}
          categories={categories || {}}
          handleSkillLevelsChange={handleSkillLevelsChange}
          handleGenresChange={handleGenresChange}
          handleTopicsChange={handleTopicsChange}
          handleTypeChange={handleTypeChange}
          lastCourseElementRef={lastCourseElementRef}
          onFavoriteChange={onFavoriteChange}
          isEnrichingData={isEnrichingData}
          filterLoading={filterLoading}
          useSkeleton={useSkeleton}
          onResetSidebarFilters={onResetSidebarFilters}
          isAnyFilterActive={isAnyFilterActive}
        />
      </div>
    </>
  );
});

export default BaseCatalogPage;
